import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import { useIsMobile } from "../components/helpers";
import Airtable from "airtable";
import { motion, AnimatePresence } from "framer-motion";
import COLORS from "../colors";
import MobileProjectDrawer from "../components/MobileProjectDrawer";
import DesktopProjectBlock from "../components/DesktopProjectBlock";

/* Helper Functions */
async function fetchAirtableData() {
  const base = new Airtable({
    apiKey: process.env.REACT_APP_AIRTABLE_API_TOKEN,
  }).base(process.env.REACT_APP_AIRTABLE_BASE_ID);

  // Fetch all records from the table
  const records = await base(process.env.REACT_APP_AIRTABLE_TABLE_NAME)
    .select({ view: "MAIN" })
    .all();
  // Convert to JSON-like structure

  const json = {
    records: records.map((rec) => ({ id: rec.id, fields: rec.fields })),
  };
  return json;
}

async function fetchAaronImage() {
  const base = new Airtable({
    apiKey: process.env.REACT_APP_AIRTABLE_API_TOKEN,
  }).base(process.env.REACT_APP_AIRTABLE_BASE_ID);

  const records = await base(process.env.REACT_APP_AIRTABLE_PERSONAL_TABLE_NAME)
    .select({
      filterByFormula: "{name} = 'aaron-picture'",
    })
    .all();

  if (records.length > 0) {
    const record = records[0];
    return record.fields.Attachments[0].url;
  } else {
    console.log('No record found with name "aaron-picture"');
    return null;
  }
}

function AnimatedEllipses() {
  const [step, setStep] = useState(0);
  const ellipses = [".", "..", "...", "...."];
  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => (prev + 1) % ellipses.length);
    }, 400);
    return () => clearInterval(interval);
  });
  return <span>{ellipses[step]}</span>;
}

function MoreWork() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [workSections, setWorkSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aaronImageUrl, setAaronImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const isMobile = useIsMobile();

  // Function to prefetch images
  const prefetchImages = async (projectInfo) => {
    if (!projectInfo || !projectInfo.images || projectInfo.images.length === 0)
      return;

    const imagePromises = projectInfo.images.map((img) => {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.src = img.url || img;
        image.onload = resolve;
        image.onerror = reject;
      });
    });

    try {
      await Promise.all(imagePromises);
    } catch (error) {
      console.error("Error prefetching images:", error);
    }
  };

  // Function to prefetch all project images
  const prefetchAllImages = async (sections) => {
    const allProjects = sections.flatMap((section) => section.items);
    const prefetchPromises = allProjects.map((project) => {
      if (project.images && project.images.length > 0) {
        return prefetchImages({ images: project.images });
      }
      return Promise.resolve();
    });

    try {
      await Promise.all(prefetchPromises);
      console.log("All project images prefetched successfully");
    } catch (error) {
      console.error("Error prefetching all images:", error);
    }
  };

  // Simplified project selection handler (no more prefetching delay)
  const handleProjectSelect = (projectName) => {
    setSelectedProject(projectName);
  };

  // Fetch Airtable data on mount
  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchAirtableData().then((json) => {
        if (json.records) {
          // Group by type (section)
          const grouped = {};
          json.records.forEach((rec) => {
            const section = rec.fields.type || "Other";
            if (!grouped[section]) grouped[section] = [];
            grouped[section].push(rec.fields); // Store full fields object
          });
          const sections = Object.keys(grouped).map((section) => ({
            title: section,
            items: grouped[section], // Each item is a full project object
          }));
          setWorkSections(sections);

          // Prefetch all images in the background
          prefetchAllImages(sections);
        }
        setLoading(false);
      }),
      fetchAaronImage().then((imageUrl) => {
        if (imageUrl) {
          setAaronImageUrl(imageUrl);
        }
        setImageLoading(false);
      }),
    ]).then(() => {
      setDataLoaded(true);
    });

    // Set initial load to false after 1 second
    setTimeout(() => {
      setInitialLoad(false);
    }, 1500);
  }, []);

  // Only show content when both initial load timer and data are ready
  const showContent = !initialLoad && dataLoaded;

  // Only allow scroll on mobile, lock scroll on desktop
  useEffect(() => {
    if (isMobile && selectedProject) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
    if (!isMobile) {
      document.body.style.overflow = "hidden";
      return undefined;
    }
    // If mobile and no drawer, allow scroll
    document.body.style.overflow = "auto";
    return undefined;
  }, [isMobile, selectedProject]);

  // Helper to get project info
  const getProjectInfo = (projectName) => {
    for (const section of workSections) {
      for (const item of section.items) {
        if (item.title === projectName) {
          // Return the full project object
          return {
            title: item.title || "",
            type: item.type || "",
            role: item.role || "",
            source: item.source || "",
            year: item.year || "",
            link: item.link || "",
            description: item.description || "",
            images: item.images || [],
          };
        }
      }
    }
    // fallback
    return {
      title: projectName,
      type: "",
      role: "",
      source: "",
      year: "",
      link: "",
      description: "",
      images: [],
    };
  };

  const renderMobileView = () => {
    return (
      <>
        <MobileContentWrapper>
          <AnimatePresence mode="wait">
            {!showContent ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                }}
              >
                <LoadingText>
                  Gathering projects
                  <AnimatedEllipses />
                </LoadingText>
              </motion.div>
            ) : (
              <motion.div
                key="content"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                {imageLoading ? (
                  <LoadingImage>
                    Collecting image
                    <AnimatedEllipses />
                  </LoadingImage>
                ) : (
                  <AaronImage src={aaronImageUrl} alt="Aaron Edwards" />
                )}
                <MobileProjectList>
                  {workSections.map((section) => (
                    <div key={section.title}>
                      <MobileSectionTitle>{section.title}</MobileSectionTitle>
                      <MobileSectionList>
                        {section.items.map((item, idx) => (
                          <MobileSectionListItem
                            key={item.title + idx}
                            onClick={() => handleProjectSelect(item.title)}
                          >
                            {item.title}
                          </MobileSectionListItem>
                        ))}
                      </MobileSectionList>
                    </div>
                  ))}
                </MobileProjectList>
              </motion.div>
            )}
          </AnimatePresence>
        </MobileContentWrapper>
        <MobileProjectDrawer
          open={!!selectedProject}
          onClose={() => setSelectedProject(null)}
          projectInfo={selectedProject ? getProjectInfo(selectedProject) : {}}
        />
      </>
    );
  };

  const renderDesktopView = () => {
    return (
      <>
        <MainContent>
          <AnimatePresence mode="wait">
            {!showContent ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                }}
              >
                <LoadingText>
                  Gathering projects
                  <AnimatedEllipses />
                </LoadingText>
              </motion.div>
            ) : (
              <motion.div
                key="content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <AnimatePresence mode="wait">
                  {!selectedProject ? (
                    <motion.div
                      key="list"
                      initial={{ x: 400 }}
                      animate={{ x: 0 }}
                      exit={{ x: 400 }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                      style={{
                        position: "fixed",
                        bottom: 0,
                        right: 0,
                        zIndex: 10,
                      }}
                    >
                      <RightColumn>
                        {loading ? (
                          <LoadingProjects>
                            Collecting projects
                            <AnimatedEllipses />
                          </LoadingProjects>
                        ) : (
                          workSections.map((section) => (
                            <WorkSection key={section.title}>
                              <SectionTitle>{section.title}</SectionTitle>
                              <SectionList>
                                {section.items.map((item, idx) => {
                                  const isActive =
                                    selectedProject === item.title;
                                  return (
                                    <SectionListItem
                                      key={item.title + idx}
                                      $active={isActive}
                                      onClick={() => {
                                        if (isActive) {
                                          setSelectedProject(null);
                                        } else {
                                          handleProjectSelect(item.title);
                                        }
                                      }}
                                    >
                                      {isActive && (
                                        <CloseX
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedProject(null);
                                          }}
                                        >
                                          ×
                                        </CloseX>
                                      )}
                                      {item.title}
                                    </SectionListItem>
                                  );
                                })}
                              </SectionList>
                            </WorkSection>
                          ))
                        )}
                      </RightColumn>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="back-button"
                      initial={{ x: -400, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: -400, opacity: 0 }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                      style={{
                        position: "fixed",
                        bottom: 0,
                        right: 0,
                        zIndex: 10,
                      }}
                    >
                      <BackButton onClick={() => setSelectedProject(null)}>
                        ← Back to List
                      </BackButton>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </MainContent>
        <AnimatePresence mode="wait">
          {!showContent ? (
            <motion.div
              key="loading-image"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0 }}
              transition={{ duration: 0 }}
            />
          ) : !selectedProject ? (
            <motion.div
              key="aaron-image"
              initial={{ x: -600 }}
              animate={{ x: 0 }}
              exit={{ x: -600 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            >
              <ProjectViewOverlay>
                <AaronImageBlock>
                  {imageLoading ? (
                    <LoadingImage>
                      Collecting image
                      <AnimatedEllipses />
                    </LoadingImage>
                  ) : (
                    <AaronImage src={aaronImageUrl} alt="Aaron Edwards" />
                  )}
                </AaronImageBlock>
              </ProjectViewOverlay>
            </motion.div>
          ) : (
            <motion.div
              key="project-content"
              initial={{ x: -800, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -800, opacity: 0 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            >
              <ProjectViewOverlay
                style={{ pointerEvents: selectedProject ? "auto" : "none" }}
              >
                {selectedProject &&
                  getProjectInfo(selectedProject).images.length > 0 && (
                    <ProjectImagesRow>
                      {getProjectInfo(selectedProject).images.map((img, i) => (
                        <ProjectImage
                          key={i}
                          src={img.url || img}
                          alt={
                            getProjectInfo(selectedProject).title +
                            " image " +
                            (i + 1)
                          }
                        />
                      ))}
                    </ProjectImagesRow>
                  )}

                <DesktopProjectBlock
                  selectedProject={selectedProject}
                  getProjectInfo={getProjectInfo}
                />
              </ProjectViewOverlay>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  };

  return (
    <ArchiveWrapper>
      <Navbar />
      {isMobile ? renderMobileView() : renderDesktopView()}
    </ArchiveWrapper>
  );
}

export default MoreWork;

/* Page Styles */

export const AaronImageBlock = styled.div`
  width: 500px;
  max-width: 500px;
  display: flex;
  height: 100%;
  align-items: flex-end;
  background: none;
`;

const ArchiveWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin: 0px 50px;
  margin-top: 40px;

  @media only screen and (max-width: 600px) {
    margin: 20px;
    margin-top: 30px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 1300px;
  justify-content: space-between;
  align-items: flex-end;
`;

const RightColumn = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 40px;
  min-width: 320px;
  width: 450px;
  padding: 50px 50px 50px 0;
  background: none;
  z-index: 10;
`;

const WorkSection = styled.div`
  width: 100%;
`;

const SectionTitle = styled.div`
  font-size: 20px;
  font-weight: 400;
  color: ${COLORS.darkGray};
  font-style: italic;
  margin-bottom: 8px;
  text-align: right;
`;

const SectionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: right;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const SectionListItem = styled.li`
  font-size: 18px;
  font-family: forma-djr-deck, sans-serif;
  font-weight: 400;
  color: ${({ $active }) => ($active ? "#e97522" : "#111")};
  cursor: pointer;
  transition: color 0.2s;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  &:hover {
    color: #e97522;
  }
`;

const CloseX = styled.span`
  color: ${COLORS.orange};
  font-size: 35px;
  line-height: 20px;
  font-weight: 400;
  margin-right: 8px;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;
  &:hover {
    color: #b34c00;
  }
`;

// --- Project View Overlay Styles ---
const ProjectViewOverlay = styled.div`
  position: fixed;
  top: 80px;
  left: 0;
  width: 100vw;
  height: calc(100vh - 80px);
  z-index: 5;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 50px 0 50px 50px;
  pointer-events: ${(props) => props.style?.pointerEvents || "auto"};
  gap: 50px;
`;

const ProjectImagesRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100vw;
  gap: 24px;
  justify-content: flex-start;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding-right: 100px;
`;

const ProjectImage = styled.img`
  height: 100%;
  max-height: 400px;
  width: auto;
  object-fit: contain;
  border-radius: 6px;
  background: #d9d9d9;
`;

const AaronImage = styled.img`
  width: 40vw;
  max-width: 400px;
  max-height: 450px;
  height: auto;
  object-fit: cover;
  border-radius: 4px;

  @media only screen and (max-width: 600px) {
    width: 250px;
    height: 300px;
    margin: 75px 0;
  }
`;

const MobileContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: 80px;
`;

const MobileProjectList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 50px;
`;

const MobileSectionTitle = styled.div`
  font-size: 20px;
  font-weight: 400;
  color: ${COLORS.darkGray};
  font-style: italic;
  margin-bottom: 8px;
`;

const MobileSectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MobileSectionListItem = styled.div`
  font-size: 18px;
  font-family: forma-djr-deck, sans-serif;
  font-weight: 400;
  color: #111;
  cursor: pointer;
  transition: color 0.2s;
  text-align: left;
  width: fit-content;

  &:hover {
    color: #e97522;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 50px 50px 50px 0;
  font-family: forma-djr-deck, sans-serif;
  color: #111;
  transition: color 0.2s;
  &:hover {
    color: #e97522;
  }
`;

const LoadingProjects = styled.div`
  font-size: 18px;
  font-family: forma-djr-deck, sans-serif;
  color: ${COLORS.darkGray};
  text-align: right;
  font-weight: 400;
`;

const LoadingImage = styled.div`
  font-size: 18px;
  font-family: forma-djr-deck, sans-serif;
  color: ${COLORS.darkGray};
  text-align: right;
  font-weight: 400;
`;

const LoadingText = styled.div`
  font-size: 18px;
  font-family: forma-djr-deck, sans-serif;
  color: ${COLORS.darkGray};
  text-align: right;
  font-weight: 400;
`;
